// react
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

// material-ui
import { Autocomplete, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { useRecoilState } from 'recoil';
import { AcademyState } from 'states/Academy';

const AcademyBar = ({ onChange }, ref) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [academyState, setAcademyState] = useRecoilState(AcademyState)
    const [academies, setAcademies] = useState([])
    const [keyword, setKeyword] = useState("")
    const [initialLoadDone, setInitialLoadDone] = useState(false)

    // URL에서 academy_name 파라미터 읽기
    const urlAcademyName = searchParams.get('academy_name')

    // URL academy_name 파라미터 업데이트
    const updateUrlAcademyName = useCallback((academyName) => {
        const newParams = new URLSearchParams(searchParams)
        if (academyName) {
            newParams.set('academy_name', academyName)
        } else {
            newParams.delete('academy_name')
        }
        // page 파라미터는 학원 변경 시 초기화
        newParams.delete('page')
        setSearchParams(newParams, { replace: true })
    }, [searchParams, setSearchParams])
    function load(keyword) {
        const params = {
            name: keyword
        }
        axios
            .get('/users/academies/', { params })
            .then(response => {
                let results = response.data
                if(results == null) {
                    return
                }
                setAcademies(results)
            }).catch(error => {
                toast.error("조회 중 오류가 발생했습니다.\n" + JSON.stringify(error.response.data))
            });
    }

    function onRefresh() {
        setKeyword("")
    }

    function onTextChange(e, value) {
        setKeyword(value)
    }
    
    function onAcademySelect(e, value) {
        updateAcademyState(value)
    }

    function onTextKeyDown(e) {
        if (e.key === 'Enter' || e.keyCode === 13) {
            const academyName = e.target.value
            const academyFiltered = academies.filter((academy) => (academy.name === academyName))
            if(academyFiltered != null && academyFiltered.length > 0) {
                updateAcademyState(academyFiltered[0])
            }
        }
    }

    function updateAcademyState(academy, updateUrl = true) {
        setAcademyState(academy)
        if (updateUrl) {
            updateUrlAcademyName(academy?.name || null)
        }
    }

    ref.onRefresh = onRefresh

    useEffect(() => {
        load(keyword)
    }, [keyword])

    // URL에 academy_name이 있고 아직 학원이 로드되지 않은 경우 로드
    useEffect(() => {
        if (urlAcademyName && !initialLoadDone) {
            load(urlAcademyName)
        }
    }, [urlAcademyName, initialLoadDone])

    // academies가 로드된 후 URL의 academy_name에 해당하는 학원 선택
    useEffect(() => {
        if (urlAcademyName && academies.length > 0 && !initialLoadDone) {
            const matchedAcademy = academies.find(a => a.name === urlAcademyName)
            if (matchedAcademy && academyState?.name !== matchedAcademy.name) {
                setAcademyState(matchedAcademy)
            }
            setInitialLoadDone(true)
        }
    }, [urlAcademyName, academies, initialLoadDone])

    // URL academy_name 변경 시 동기화 (이미 초기화 완료된 후)
    useEffect(() => {
        if (initialLoadDone && urlAcademyName !== academyState?.name) {
            if (urlAcademyName) {
                const matchedAcademy = academies.find(a => a.name === urlAcademyName)
                if (matchedAcademy) {
                    setAcademyState(matchedAcademy)
                }
            } else {
                setAcademyState(null)
            }
        }
    }, [urlAcademyName, initialLoadDone])

    return <div ref={ref}>
            <Autocomplete
            id="academy-select"
            sx={{ width: 300 }}
            options={academies}
            value={academyState}
            autoHighlight
            onChange={onAcademySelect}
            onInputChange={onTextChange}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name}
              </Box>
            )}
            renderInput={(params) => {
              return <TextField
              onKeyDown={onTextKeyDown}
              autoComplete='off'
                {...params}
                label="학원을 관리하시려면 선택해주세요"
                inputProps={{
                  ...params.inputProps
                }}
              />
            }}
          />
        </div>
};

export default React.forwardRef(AcademyBar);